import React, { useState, useEffect } from 'react'
import { Grid, TextField, Typography } from '@mui/material'
import { ComponentProps } from '../../../components/_WMS/FilterComponent/types'

interface DateRangeValue {
  initialCreatedDate: string
  finalCreatedDate: string
}

export const CreatedDateRangePicker: React.FC<ComponentProps> = ({
  value,
  onChange,
  fullWidth = true,
  variant = 'outlined'
}) => {
  const [initialDate, setInitialDate] = useState('')
  const [finalDate, setFinalDate] = useState('')
  const [dateRangeError, setDateRangeError] = useState('')

  // Parsear valor inicial si existe
  useEffect(() => {
    if (value) {
      try {
        const parsed: DateRangeValue = JSON.parse(value)
        setInitialDate(parsed.initialCreatedDate || '')
        setFinalDate(parsed.finalCreatedDate || '')
      } catch {
        // Si no es JSON válido, ignorar
      }
    }
  }, [])

  const validateDateRange = (start: string, end: string) => {
    if (start && end) {
      const startDate = new Date(start)
      const endDate = new Date(end)

      if (startDate > endDate) {
        setDateRangeError('La fecha inicial no puede ser mayor que la fecha final')
        return false
      } else {
        setDateRangeError('')
        return true
      }
    }
    setDateRangeError('')
    return true
  }

  const handleDateChange = (type: 'initial' | 'final', newValue: string) => {
    let newInitial = type === 'initial' ? newValue : initialDate
    let newFinal = type === 'final' ? newValue : finalDate

    if (type === 'initial') {
      setInitialDate(newValue)
    } else {
      setFinalDate(newValue)
    }

    // Validar rango
    const isValid = validateDateRange(newInitial, newFinal)

    // Si ambas fechas están completas y son válidas, notificar al FilterComponent
    if (newInitial && newFinal && isValid) {
      const dateRangeValue: DateRangeValue = {
        initialCreatedDate: newInitial,
        finalCreatedDate: newFinal
      }

      // Simular evento para compatibilidad con FilterComponent
      const syntheticEvent = {
        target: {
          value: JSON.stringify(dateRangeValue),
          name: 'createdDateRange'
        }
      } as React.ChangeEvent<HTMLInputElement>

      onChange(syntheticEvent)
    }
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <TextField
          type='date'
          label='Fecha Inicial'
          value={initialDate}
          onChange={(e) => handleDateChange('initial', e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth={fullWidth}
          variant={variant}
          size='small'
          error={!!dateRangeError}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          type='date'
          label='Fecha Final'
          value={finalDate}
          onChange={(e) => handleDateChange('final', e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth={fullWidth}
          variant={variant}
          size='small'
          error={!!dateRangeError}
        />
      </Grid>
      {dateRangeError && (
        <Grid item xs={12}>
          <Typography
            variant='caption'
            color='error'
            style={{ fontSize: '0.75rem' }}
          >
            {dateRangeError}
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
